import type { SortOptions } from '@tunasong/models'
import type { Entity, EntityType } from '@tunasong/schemas'
import cn from 'classnames'
import type { FC, HTMLAttributes } from 'react'
import { SortMenu } from './sort-menu.js'
import styles from './sort-filter-bar.module.css'
import { TypeFilterChips } from './type-filter-chips.js'

export interface SortFilterBarProps extends HTMLAttributes<HTMLDivElement> {
  /** All entities in the list, before filtering. Used for the chips and their counts. */
  entities: Pick<Entity, 'type'>[]
  filter: EntityType[]
  sortOptions: SortOptions
  /** Leave the sort menu out, e.g. when the host shows its own `SortMenu` on a heading row. @default false */
  hideSort?: boolean
  onFilter(filter: EntityType[]): void
  onSort(options: SortOptions): void
}

/** Toolbar above an entity list: type filter chips on the left, the sort menu on the right */
export const SortFilterBar: FC<SortFilterBarProps> = props => {
  const { entities, filter, onFilter, sortOptions, onSort, hideSort = false, className, ...restProps } = props

  const shown = filter.length > 0 ? entities.filter(e => filter.includes(e.type)).length : entities.length

  return (
    <div className={cn(styles.root, className)} {...restProps}>
      <div className={styles.toolbar}>
        <TypeFilterChips className={styles.chips} entities={entities} value={filter} onChange={onFilter} />
        {hideSort ? null : <SortMenu value={sortOptions} onChange={onSort} />}
      </div>
      {filter.length > 0 ? (
        <div className={styles.summary}>
          <span>
            Showing {shown} of {entities.length}
          </span>
          <button type="button" className={styles.clear} onClick={() => onFilter([])}>
            Clear filter
          </button>
        </div>
      ) : null}
    </div>
  )
}

export default SortFilterBar
